import { useList, useGo } from '@refinedev/core';
import { useState, useMemo, type MouseEvent } from 'react';
import {
  Search,
  GraduationCap,
  Send,
  Award,
  Users,
  User,
  Eye,
  ShieldOff,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { UserEntitlementRow, EntitlementDetail, FamilyMemberDetail, BonusDetail } from './types';

const SUBSCRIPTION_STATUS_MAP: Record<string, { label: string; className: string }> = {
  active: { label: 'Активна', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  pending: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  expired: { label: 'Истекла', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  cancelled: { label: 'Отменена', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

type DialogState =
  | { type: 'entitlements'; title: string; icon: React.ReactNode; items: EntitlementDetail[] }
  | { type: 'bonuses'; bonuses: BonusDetail[] }
  | { type: 'family'; userName: string; members: FamilyMemberDetail[]; membersCount: number; familySlots: number }
  | null;

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}


function getMemberName(member: FamilyMemberDetail): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
  return name || (member.username ? `@${member.username}` : `ID ${member.userId}`);
}

function formatBonusValue(type: string, value: unknown): string {
  if (type === 'BOOLEAN') {
    return value === true ? 'Да' : 'Нет';
  }
  if (type === 'NUMBER') {
    return String(value);
  }
  if (type === 'ARRAY' && Array.isArray(value)) {
    return value.join(', ');
  }
  if (type === 'STRING') {
    return String(value);
  }
  return String(value);
}

function ClickableCount({
  count,
  icon,
  onClick,
}: {
  count: number;
  icon: React.ReactNode;
  onClick: (e: MouseEvent) => void;
}) {
  if (count === 0) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {icon}
        <span>0</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors cursor-pointer"
      onClick={onClick}
    >
      {icon}
      <span className="font-medium underline decoration-dotted underline-offset-2">{count}</span>
    </button>
  );
}

export function UserEntitlementsList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState('user-entitlements:page', 1);
  const [search, setSearch] = usePersistedState('user-entitlements:search', '');
  const [hasActiveSubscription, setHasActiveSubscription] = usePersistedState<string>('user-entitlements:hasActiveSubscription', 'all');
  const [expiringWithin, setExpiringWithin] = usePersistedState<string>('user-entitlements:expiringWithin', 'all');
  const [sort, setSort] = usePersistedState<SortState | undefined>('user-entitlements:sort', undefined);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const pageSize = 20;

  const queryFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq' | 'contains'; value: string | number | boolean }> = [];

    if (search) {
      result.push({ field: 'search', operator: 'contains' as const, value: search });
    }
    if (hasActiveSubscription !== 'all') {
      result.push({
        field: 'hasActiveSubscription',
        operator: 'eq' as const,
        value: hasActiveSubscription === 'yes',
      });
    }
    if (expiringWithin !== 'all') {
      result.push({
        field: 'expiringWithin',
        operator: 'eq' as const,
        value: Number(expiringWithin),
      });
    }

    return result;
  }, [search, hasActiveSubscription, expiringWithin]);

  const listResult = useList<UserEntitlementRow>({
    resource: 'user-entitlements',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const openEntitlementsDialog = (e: MouseEvent, title: string, icon: React.ReactNode, items: EntitlementDetail[] | undefined) => {
    e.stopPropagation();
    const safeItems = items ?? [];
    if (safeItems.length === 0) {
      return;
    }
    setDialogState({ type: 'entitlements', title, icon, items: safeItems });
  };

  const openBonusesDialog = (e: MouseEvent, bonuses: BonusDetail[] | undefined) => {
    e.stopPropagation();
    const safeBonuses = bonuses ?? [];
    if (safeBonuses.length === 0) {
      return;
    }
    setDialogState({ type: 'bonuses', bonuses: safeBonuses });
  };

  const openFamilyDialog = (e: MouseEvent, row: UserEntitlementRow) => {
    e.stopPropagation();
    if (!row.subscription) {
      return;
    }
    const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ');
    setDialogState({
      type: 'family',
      userName: fullName || (row.username ? `@${row.username}` : `ID ${row.id}`),
      members: row.subscription.familyMembers ?? [],
      membersCount: row.subscription.familyMembersCount,
      familySlots: row.subscription.familySlots,
    });
  };

  const columns: Column<UserEntitlementRow>[] = [
    {
      key: 'firstName',
      header: 'Пользователь',
      width: '200px',
      sortable: true,
      render: (_, row) => {
        const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ');
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-sm truncate">
              {fullName || `ID ${row.id}`}
            </span>
            {row.username && (
              <span className="text-xs text-muted-foreground truncate">
                @{row.username}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'subscription',
      header: 'Подписка',
      width: '220px',
      render: (_, row) => {
        if (!row.subscription) {
          return (
            <Badge variant="secondary">Нет подписки</Badge>
          );
        }
        const { planName, planCode, status, endDate } = row.subscription;
        const statusInfo = SUBSCRIPTION_STATUS_MAP[status] ?? { label: status, className: '' };
        const displayName = planName || planCode || statusInfo.label;
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className={statusInfo.className}>
                {statusInfo.label}
              </Badge>
            </div>
            <span className="text-xs font-medium truncate" title={displayName ?? undefined}>
              {displayName}
            </span>
            {endDate && (
              <span className="text-xs text-muted-foreground">
                до {formatDate(endDate)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'family',
      header: 'Семейная',
      width: '110px',
      render: (_, row) => {
        if (!row.subscription?.isFamilyOwner) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors cursor-pointer"
            onClick={(e) => openFamilyDialog(e, row)}
          >
            <Users className="h-3.5 w-3.5 text-blue-500" />
            <span className="font-medium underline decoration-dotted underline-offset-2">
              {row.subscription.familyMembersCount}/{row.subscription.familySlots}
            </span>
          </button>
        );
      },
    },
    {
      key: 'coursesCount',
      header: 'Курсы',
      width: '100px',
      sortable: true,
      render: (_, row) => (
        <ClickableCount
          count={row.coursesCount}
          icon={<GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />}
          onClick={(e) => openEntitlementsDialog(e, 'Курсы', <GraduationCap className="h-5 w-5" />, row.courses)}
        />
      ),
    },
    {
      key: 'channelsCount',
      header: 'Каналы',
      width: '100px',
      sortable: true,
      render: (_, row) => (
        <ClickableCount
          count={row.channelsCount}
          icon={<Send className="h-3.5 w-3.5 text-muted-foreground" />}
          onClick={(e) => openEntitlementsDialog(e, 'Каналы', <Send className="h-5 w-5" />, row.channels)}
        />
      ),
    },
    {
      key: 'bonusesCount',
      header: 'Привилегии',
      width: '100px',
      sortable: true,
      render: (_, row) => (
        <ClickableCount
          count={row.bonusesCount}
          icon={<Award className="h-3.5 w-3.5 text-muted-foreground" />}
          onClick={(e) => openBonusesDialog(e, row.bonuses)}
        />
      ),
    },
  ];

  const bulkActions: BulkAction<UserEntitlementRow>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/users/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Доступы',
      icon: <ShieldOff className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/users/show/${items[0].id}?tab=access` });
        }
      },
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Доступы пользователей"
        description="Подписки, курсы, каналы и привилегии пользователей"
        sticky
      />

      {/* Поиск и фильтры */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Поиск по имени или username..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
        </div>

        <Select
          value={hasActiveSubscription}
          onValueChange={(v) => {
            setHasActiveSubscription(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Подписка" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="yes">С подпиской</SelectItem>
            <SelectItem value="no">Без подписки</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={expiringWithin}
          onValueChange={(v) => {
            setExpiringWithin(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[210px]">
            <SelectValue placeholder="Истекает" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все</SelectItem>
            <SelectItem value="7">В течение 7 дней</SelectItem>
            <SelectItem value="30">В течение 30 дней</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => go({ to: `/users/show/${row.id}?tab=access` })}
        emptyMessage="Нет данных о доступах пользователей"
        sort={sort}
        onSortChange={setSort}
        selectable
        bulkActions={bulkActions}
      />

      {/* Модалка деталей доступов (курсы / каналы) */}
      <Dialog open={dialogState?.type === 'entitlements'} onOpenChange={(open) => { if (!open) { setDialogState(null); } }}>
        {dialogState?.type === 'entitlements' && (
          <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {dialogState.icon}
                {dialogState.title}
                <Badge variant="secondary">{dialogState.items.length}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="divide-y">
                {dialogState.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 py-2.5 px-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Модалка привилегий */}
      <Dialog open={dialogState?.type === 'bonuses'} onOpenChange={(open) => { if (!open) { setDialogState(null); } }}>
        {dialogState?.type === 'bonuses' && (
          <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Привилегии
                <Badge variant="secondary">{dialogState.bonuses.length}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="divide-y">
                {dialogState.bonuses.map((bonus) => (
                  <div key={bonus.id} className="flex items-center justify-between gap-3 py-2.5 px-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{bonus.name}</p>
                      <p className="text-xs text-muted-foreground">{bonus.type}</p>
                    </div>
                    <Badge variant="outline" className="flex-shrink-0">
                      {formatBonusValue(bonus.type, bonus.currentValue)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Модалка семейного доступа */}
      <Dialog open={dialogState?.type === 'family'} onOpenChange={(open) => { if (!open) { setDialogState(null); } }}>
        {dialogState?.type === 'family' && (
          <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Семейный доступ
                <Badge variant="secondary">{dialogState.membersCount}/{dialogState.familySlots}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0">
              {dialogState.members.length > 0 ? (
                <div className="divide-y">
                  {dialogState.members.map((member) => (
                    <button
                      key={member.userId}
                      type="button"
                      className="flex items-center gap-3 py-2.5 px-1 w-full text-left hover:bg-muted/50 transition-colors"
                      onClick={() => go({ to: `/users/show/${member.userId}` })}
                    >
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{getMemberName(member)}</p>
                        {member.username && (
                          <p className="text-xs text-muted-foreground">@{member.username}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  Нет участников
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
